import { isBlacklistedCountry } from '../../models/countries'
import { columnTypes } from '../../models/db'
import { GameAssetVariant } from '../../models/db/tables'
import { GameId } from '../../models/game'
import { SCIDChronosOffer } from '../../models/offer'
import type { Prices } from '../../models/product'
import { StoreError, StoreErrorMessage } from '../../utils/StoreError'
import type { Queries } from '../postgres/createPostgresQueries'
import type { Logger } from 'pino'
import ZERO_PRICE_MAP from '../../utils/zeroPriceMap'
import { DEFAULT_CURRENCY_COUNTRY } from '../../models/db/columnTypes'
import { isDefined } from '../../utils/isDefined'

const CHAIN_OFFER_INTERNAL_BILLING_PACKAGES = new Set(['com.supercell.laser.scid.internal.chainoffer'])

type Dependencies = {
  logger: Logger
}

type AdminServiceConfig = {
  useLocalManagedAssets: boolean
  managedAssetsApi: {
    url: string
    apiKey: string
    cdnUrl: string
  }
}

export type AdminService = ReturnType<typeof createAdminService>
export function createAdminService(queries: Queries, { logger }: Dependencies, getConfig: () => AdminServiceConfig) {
  return {
    getPricesForBillingPackages: getPricesForBillingPackages(queries, logger),
    getMarket: getMarket(queries, logger),
    requireEnabledMarket: requireEnabledMarket(queries, logger),
    getCountryCodes: queries.getCountryCodes,
    getSupportedCurrencies: queries.getSupportedCurrencies,
    getActiveAnnouncement: queries.getActiveAnnouncement,
    getCreatorGiveawayAccount: queries.getCreatorGiveawayAccount,
    getAssetsForOffers: getAssetsForOffers(queries, logger, getConfig),
    getAssetsForGlobalIds: getOffersForGlobalIds(queries, logger, getConfig),
    isTestAccount: queries.isTestAccount,
    isInMaintenance: queries.isInMaintenance,
  }
}

function getPricesForBillingPackages(queries: Queries, logger: Logger) {
  return async (skus: string[], countryCode: string, currencyCode: string) => {
    if (skus.length === 0) {
      return {}
    }

    try {
      const prices = await queries.getPricesForSKUs(skus, countryCode, currencyCode)
      const priceMap = prices.reduce(
        (acc, cur) => {
          const currencyCountry = `${cur.currencyCode}-${cur.countryCode}`
          const pricesForSku = Object.assign({}, acc[cur.sku], { [currencyCountry]: cur.price })

          acc[cur.sku] = pricesForSku
          return acc
        },
        {} as Record<string, Prices>,
      )

      const chainOfferSKU = skus.find((sku) => CHAIN_OFFER_INTERNAL_BILLING_PACKAGES.has(sku))
      if (chainOfferSKU) {
        priceMap[chainOfferSKU] = Object.fromEntries(
          Object.entries(ZERO_PRICE_MAP).filter(
            ([currencyCountry]) =>
              currencyCountry === `${currencyCode}-${countryCode}` || currencyCountry === DEFAULT_CURRENCY_COUNTRY,
          ),
        )
      }

      return priceMap
    } catch (err) {
      logger.error({ err }, `Unable to fetch prices for SKUs`)
      throw StoreError.withMessage(StoreErrorMessage.PRODUCT_PRICE_NOT_FOUND).withBody(
        `Unable to fetch prices for SKUs`,
      )
    }
  }
}

function getMarket(queries: Queries, logger: Logger) {
  return async (countryCode: string) => {
    const countryCodeParseResult = columnTypes.countryCodeSchema.safeParse(countryCode)
    try {
      if (countryCodeParseResult.success && isBlacklistedCountry(countryCodeParseResult.data)) {
        return undefined
      }
      return await queries.getMarket(countryCode)
    } catch (err) {
      if (countryCodeParseResult.success && !isBlacklistedCountry(countryCodeParseResult.data)) {
        logger.info({ err }, `Failed to get country ${countryCode}, returning default market`)
        return toDefaultMarket(countryCodeParseResult.data)
      }
      logger.error({ err }, `Failed to get country ${countryCode}, returning undefined`)
      return undefined
    }
  }
}

function toDefaultMarket(countryCode: columnTypes.CountryCode) {
  return {
    countryCode,
    currencyCode: columnTypes.DEFAULT_CURRENCY_CODE,
    currencyCountry: columnTypes.DEFAULT_CURRENCY_COUNTRY,
    appStoreTerritory: 'US',
    enabled: !isBlacklistedCountry(countryCode),
    paymentGateway: 'FASTSPRING',
    taxType: 'VAT',
    tax: null,
    taxRate: 0,
    currencyDecimals: 2,
  } as const
}

function requireEnabledMarket(queries: Queries, logger: Logger) {
  const _getMarket = getMarket(queries, logger)

  return async (countryCode: string) => {
    const market = await _getMarket(countryCode)

    if (!market?.enabled) {
      logger.error(`Country ${countryCode} is not enabled`)
      throw StoreError.withMessage(StoreErrorMessage.COUNTRY_NOT_FOUND)
    }

    return market
  }
}

async function getAssetsFromProdUpstream(
  logger: Logger,
  config: AdminServiceConfig,
  globalIds: string[],
  game: GameId,
): Promise<Record<string, GameAssetVariant[]>> {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${config.managedAssetsApi.apiKey}`,
  }
  const response = await fetch(
    `${config.managedAssetsApi.url}/${game}/assets?globalIds=${globalIds.filter((id) => isDefined(id) && id.length > 0).join(',')}`,
    {
      method: 'GET',
      headers,
    },
  )

  if (!response.ok) {
    logger.error(`Unable to resolve assets for offers from upstream`, {
      status: response.status,
      statusText: await response.text(),
    })
    return {}
  }

  return response.json()
}

function getAssetsForOffers(queries: Queries, logger: Logger, getConfig: () => AdminServiceConfig) {
  return async (scidChronosOffers: SCIDChronosOffer[], game: GameId) => {
    if (!scidChronosOffers) {
      return {}
    }

    const globalIds = scidChronosOffers.flatMap((offer) =>
      offer.skus.flatMap((sku) => sku.contents.map((content) => content.product.id.split(':').reverse()[0])),
    )
    try {
      // Fetch assets from prod in other environments
      if (!getConfig().useLocalManagedAssets) {
        return getAssetsFromProdUpstream(logger, getConfig(), globalIds, game)
      }

      const assets = await queries.getAssetsForOffers(globalIds, game)
      return assets.reduce(
        (acc, cur) => {
          acc[cur.globalId] = cur.variants.map((variant) => ({
            ...variant,
            fileKey: `${getConfig().managedAssetsApi.cdnUrl}/${variant.fileKey}`,
          }))

          return acc
        },
        {} as Record<string, GameAssetVariant[]>,
      )
    } catch (err) {
      logger.error(`Unable to resolve assets for offers`, err)
      throw StoreError.withMessage(StoreErrorMessage.INTERNAL_SERVER_ERROR).withBody(
        `Unable to fetch assets for offers`,
      )
    }
  }
}

function getOffersForGlobalIds(queries: Queries, logger: Logger, getConfig: () => AdminServiceConfig) {
  return async (globalIds: string[], game: GameId) => {
    try {
      // Fetch assets from prod in other environments
      if (!getConfig().useLocalManagedAssets) {
        return getAssetsFromProdUpstream(logger, getConfig(), globalIds, game)
      }

      const assets = await queries.getAssetsForOffers(globalIds, game)
      return assets.reduce(
        (acc, cur) => {
          acc[cur.globalId] = cur.variants.map((variant) => ({
            ...variant,
            fileKey: `${getConfig().managedAssetsApi.cdnUrl}/${variant.fileKey}`,
          }))

          return acc
        },
        {} as Record<string, GameAssetVariant[]>,
      )
    } catch (err) {
      logger.error(`Unable to resolve assets for offers`, err)
      throw StoreError.withMessage(StoreErrorMessage.INTERNAL_SERVER_ERROR).withBody(
        `Unable to fetch assets for offers`,
      )
    }
  }
}
